import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Compass, RefreshCw, Building2, Eye, Scale, HeartHandshake } from "lucide-react";
import { useLanguage, tr } from "../i18n";
import { lp } from "../components/Navbar";
import { MaskedLine, Reveal, Stagger, StaggerItem, Kicker, SectionHead } from "../components/motion";
import { CTABand } from "./Shared";
import { articles } from "../data/articles";
const CYCLE = [{
  n: "01",
  title: "Discover",
  copy: "Identify AI systems, agents and relevant governance structures."
}, {
  n: "02",
  title: "Assess",
  copy: "Evaluate AI practices against the applicable ESAIA assessment framework."
}, {
  n: "03",
  title: "Identify",
  copy: "Map risks, gaps and improvement opportunities."
}, {
  n: "04",
  title: "Improve",
  copy: "Develop remediation and governance recommendations."
}, {
  n: "05",
  title: "Assure",
  copy: "Receive an ESAIA assessment report and, where applicable, certification."
}, {
  n: "06",
  title: "Reassess",
  copy: "Return annually to reassess changes in the AI environment."
}];
const PRINCIPLES = [{
  icon: Eye,
  title: "TRANSPARENT",
  strap: "Clear visibility into how AI is developed, deployed and governed.",
  copy: "ESAIA helps organizations understand their AI environment, identify relevant risks and establish transparency around AI systems, agents, governance and decision-making."
}, {
  icon: Scale,
  title: "ACCOUNTABLE",
  strap: "Clear responsibility for the development, deployment and use of AI.",
  copy: "ESAIA helps organizations establish and demonstrate appropriate accountability, ownership, oversight and responsibility throughout the AI lifecycle."
}, {
  icon: HeartHandshake,
  title: "ETHICAL",
  strap: "AI that is developed and used with responsible principles at its core.",
  copy: "ESAIA evaluates the ethical and responsible dimensions of AI use, helping organizations identify gaps and continuously improve their approach."
}];
const WHY = [{
  icon: ShieldCheck,
  title: "Independent",
  copy: "ESAIA provides an independent assessment perspective separate from the development or implementation of the AI system."
}, {
  icon: Compass,
  title: "Risk-focused",
  copy: "Assess AI-related risks across governance, operations, data, oversight and implementation."
}, {
  icon: RefreshCw,
  title: "Continuously relevant",
  copy: "AI is constantly changing. New systems, agents and capabilities keep entering your environment, so ESAIA assurance evolves with them."
}, {
  icon: Building2,
  title: "Built for organizations",
  copy: "Designed for individual organizations as well as complex corporate groups."
}];
const MODEL_STEPS = [{
  label: "Free ERI",
  sub: "Initial readiness assessment"
}, {
  label: "Certification",
  sub: "Single-Entity or Group-Wide"
}, {
  label: "12 Months",
  sub: "Certification validity"
}, {
  label: "Reassessment",
  sub: "Annual renewal cycle"
}, {
  label: "Renewal",
  sub: "Continued assurance"
}, {
  label: "Additional",
  sub: "New systems and agents"
}];
const FACTS = [{
  stat: "1 in 3 Belgian companies already use AI",
  source: "Eurostat 2025"
}, {
  stat: "Denmark leads Europe with 42% AI adoption",
  source: "Eurostat 2025"
}, {
  stat: "Large companies use AI 3x more than small ones",
  source: "Eurostat 2025"
}];
function HeroVisual() {
  const ref = useRef(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const y1 = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const y2 = useTransform(scrollYProgress, [0, 1], [80, -60]);
  const layers = [{
    label: "GOVERNANCE",
    desc: "Ownership, oversight, responsibility"
  }, {
    label: "RISK ASSESSMENT",
    desc: "Systems, agents, data, operations"
  }, {
    label: "HUMAN OVERSIGHT",
    desc: "Escalation, intervention, control"
  }, {
    label: "ASSURANCE REPORT",
    desc: "Findings, evidence, certification"
  }];
  return <div ref={ref} className="relative hidden lg:block" aria-hidden="true">
      <motion.div style={{
      y: y2
    }} className="absolute -top-10 right-6">
        <svg width="88" height="88" viewBox="0 0 96 96">
          <path d="M48 6 90 48 48 90 6 48Z" fill="none" stroke="#2B5CB8" strokeWidth="1" opacity="0.5" />
          <path d="M48 22 74 48 48 74 22 48Z" fill="#2B5CB8" opacity="0.12" />
          <path d="M48 34 62 48 48 62 34 48Z" fill="#2B5CB8" />
        </svg>
      </motion.div>
      <motion.div style={{
      y: y1
    }} className="relative border border-esaia-line bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-esaia-line px-6 py-4">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-esaia-subtle">{tr("ESAIA 01:202601")}{" "}</span>
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-esaia-blue">{tr("Assurance layers")}{" "}</span>
        </div>
        <div className="space-y-px p-6">
          {layers.map((l, i) => <motion.div key={l.label} initial={{
          opacity: 0,
          x: 32
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.7 + i * 0.15,
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1]
        }} className="group flex items-center justify-between border border-esaia-line bg-esaia-canvas px-5 py-4 transition-colors hover:border-esaia-blue/50">
              <div>
                <p className="font-mono text-xs tracking-[0.18em] text-esaia-ink">{tr(l.label)}</p>
                <p className="mt-1 text-xs text-esaia-subtle">{tr(l.desc)}</p>
              </div>
              <span className="font-serif text-2xl font-light text-esaia-blue/50">0{i + 1}</span>
            </motion.div>)}
        </div>
      </motion.div>
    </div>;
}
export default function Home() {
  const {
    lang
  } = useLanguage();
  return <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-esaia-line bg-white hairline-grid">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-20 sm:px-6 sm:py-24 lg:grid-cols-12 lg:px-8 lg:py-32">
          <div className="lg:col-span-7">
            <Reveal y={10}>
              <Kicker data-testid="hero-kicker">{tr("Ethical Standards for Artificial Intelligence Assurance")}</Kicker>
            </Reveal>
            <h1 className="mt-6 text-4xl font-semibold leading-[1.08] tracking-tight text-esaia-ink sm:text-5xl lg:text-6xl" data-testid="hero-headline">
              <MaskedLine delay={0.15}>{tr("Independent Assurance")}</MaskedLine>
              <MaskedLine delay={0.3}>{tr("for")}{" "}<span className="text-esaia-blue">{tr("Responsible AI.")}</span>
              </MaskedLine>
            </h1>
            <Reveal delay={0.55} className="mt-7 max-w-xl text-base leading-relaxed text-esaia-muted">{tr("ESAIA independently assesses how organizations develop, deploy and govern artificial intelligence, helping them identify risks, strengthen governance and demonstrate responsible AI practices.")}{" "}</Reveal>
            <Reveal delay={0.7} className="mt-10 flex flex-col gap-3 sm:flex-row">
              <Link to={lp(lang, "/eri")} data-testid="hero-start-eri-button" className="group inline-flex items-center justify-center gap-2 bg-esaia-blue px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-esaia-dark">{tr("Start Your Free ERI")}{" "}<ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to={lp(lang, "/certification")} data-testid="hero-explore-standards-button" className="inline-flex items-center justify-center gap-2 border border-esaia-ink/20 px-7 py-3.5 text-sm font-semibold text-esaia-ink transition-colors hover:border-esaia-ink hover:bg-esaia-ink hover:text-white">{tr("Explore ESAIA Certification")}{" "}</Link>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <HeroVisual />
          </div>
        </div>
      </section>

      {/* DIVIDER RIBBON */}
      <div className="overflow-hidden border-b border-esaia-line bg-white py-5" aria-hidden="true">
        <div className="marquee-track flex w-max whitespace-nowrap">
          {[0, 1].map(k => <span key={k} className="flex items-center">
              {Array.from({
            length: 3
          }).map((_, rep) => FACTS.map((f, i) => <span key={`${rep}-${i}`} className="flex items-center">
                    <span className="px-8 font-serif text-lg font-light italic text-esaia-blue/80">
                      {tr(f.stat)}
                      <span className="ml-3 align-middle font-mono text-[10px] uppercase not-italic tracking-[0.2em] text-esaia-subtle">
                        {tr(f.source)}
                      </span>
                    </span>
                    <span className="h-2 w-2 rotate-45 border border-esaia-blue/60" />
                  </span>))}
            </span>)}
        </div>
      </div>

      {/* TRUST STATEMENT */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHead kicker={tr("The assurance gap")} title={tr("AI is evolving faster than traditional governance.")} copy={tr("AI systems are becoming increasingly autonomous, interconnected and embedded in business-critical processes. New models, AI agents, datasets and applications can introduce new risks long after an initial assessment has been completed.")} />
            <Reveal delay={0.2} className="mt-6 border-l-2 border-esaia-blue pl-5">
              <p className="text-sm leading-relaxed text-esaia-ink">{tr("ESAIA provides an independent assurance framework designed to evolve with your AI environment.")}{" "}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Stagger className="grid grid-cols-1 divide-y divide-esaia-line md:grid-cols-3 md:divide-x md:divide-y-0">
              {PRINCIPLES.map(p => <StaggerItem key={p.title} className="py-9 md:px-9 md:py-1 md:first:pl-0 md:last:pr-0">
                  <div className="group" data-testid={`principle-${p.title.toLowerCase()}`}>
                    <p.icon size={22} strokeWidth={1.5} className="text-esaia-blue transition-transform duration-300 group-hover:-translate-y-1" />
                    <h3 className="mt-6 font-mono text-sm font-semibold tracking-[0.22em] text-esaia-ink">{tr(p.title)}</h3>
                    <p className="mt-4 text-sm font-medium leading-relaxed text-esaia-ink">{tr(p.strap)}</p>
                    <p className="mt-3 text-sm leading-relaxed text-esaia-muted">{tr(p.copy)}</p>
                  </div>
                </StaggerItem>)}
            </Stagger>
            <Reveal delay={0.25} className="mt-12 border-t border-esaia-line pt-8">
              <p className="font-serif text-lg font-light italic tracking-wide text-esaia-blue">{tr("Transparent. Accountable. Ethical.")}{" "}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY ESAIA */}
      <section className="border-y border-esaia-line bg-white">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
          <SectionHead kicker={tr("Why ESAIA")} title={tr("Assurance built for organizations, not for show.")} />
          <Stagger className="mt-16 grid grid-cols-1 gap-x-14 gap-y-14 sm:grid-cols-2">
            {WHY.map((w, i) => <StaggerItem key={w.title}>
                <div className="group border-t-2 border-esaia-ink pt-9" data-testid={`why-card-${i}`}>
                  <div className="flex items-baseline justify-between">
                    <span className="font-serif text-5xl font-light text-esaia-blue/70 transition-colors group-hover:text-esaia-blue">0{i + 1}</span>
                    <w.icon size={22} strokeWidth={1.5} className="text-esaia-subtle transition-colors group-hover:text-esaia-blue" />
                  </div>
                  <h3 className="mt-7 text-lg font-semibold text-esaia-ink">{tr(w.title)}</h3>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-esaia-muted">{tr(w.copy)}</p>
                </div>
              </StaggerItem>)}
          </Stagger>
        </div>
      </section>

      {/* ESAIA CYCLE */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <SectionHead kicker={tr("The ESAIA Cycle")} title={tr("Because AI does not stand still.")} copy={tr("ESAIA assurance is a continuous cycle, not a one time event. Each rotation of the cycle accounts for new systems, new agents and new risks in your AI environment.")} />
        <Stagger className="mt-16 grid grid-cols-1 gap-x-14 gap-y-14 md:grid-cols-2 lg:grid-cols-3">
          {CYCLE.map(s => <StaggerItem key={s.n}>
              <div className="group relative h-full pl-9" data-testid={`cycle-step-${s.n}`}>
                <span className="absolute left-0 top-1 h-full w-px bg-esaia-line" aria-hidden="true" />
                <span className="absolute -left-[5px] top-1 h-2.5 w-2.5 rotate-45 bg-esaia-blue transition-transform duration-300 group-hover:scale-125" aria-hidden="true" />
                <span className="font-serif text-4xl font-light text-esaia-blue/50 transition-colors group-hover:text-esaia-blue">{s.n}</span>
                <h3 className="mt-5 text-xl font-semibold text-esaia-ink">{tr(s.title)}</h3>
                <p className="mt-3 text-sm leading-relaxed text-esaia-muted">{tr(s.copy)}</p>
              </div>
            </StaggerItem>)}
        </Stagger>
        <Reveal delay={0.2} className="mt-12">
          <Link to={lp(lang, "/certification")} data-testid="cycle-explore-button" className="group inline-flex items-center gap-2 text-sm font-semibold text-esaia-blue">{tr("Explore the certification process")}{" "}<ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>

      {/* BUSINESS MODEL */}
      <section className="border-y border-esaia-line bg-white">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
          <SectionHead kicker={tr("Continuous assurance")} title={tr("AI evolves continuously. ESAIA certification is designed around continuous reassessment.")} />
          <Reveal className="mt-16">
            <ol className="grid grid-cols-1 gap-y-10 sm:grid-cols-2 lg:grid-cols-6 lg:gap-x-8">
              {MODEL_STEPS.map((s, i) => <li key={s.label} data-testid={`model-step-${i}`}>
                  <div className="flex items-center gap-4">
                    <span className="font-serif text-3xl font-light text-esaia-blue">{String(i + 1).padStart(2, "0")}</span>
                    <span className={`h-px flex-1 ${i < MODEL_STEPS.length - 1 ? "bg-esaia-line" : ""}`} aria-hidden="true" />
                  </div>
                  <p className="mt-4 text-sm font-semibold text-esaia-ink">{tr(s.label)}</p>
                  <p className="mt-1.5 text-xs leading-relaxed text-esaia-subtle">{tr(s.sub)}</p>
                </li>)}
            </ol>
          </Reveal>
          <Reveal delay={0.2} className="mt-12 flex flex-col gap-3 sm:flex-row">
            <Link to={lp(lang, "/eri")} data-testid="model-eri-button" className="inline-flex items-center justify-center gap-2 bg-esaia-blue px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-esaia-dark">{tr("Take the Free ERI")}{" "}</Link>
            <Link to={lp(lang, "/pricing")} data-testid="model-pricing-button" className="inline-flex items-center justify-center gap-2 border border-esaia-ink/20 px-6 py-3 text-sm font-semibold text-esaia-ink transition-colors hover:bg-esaia-ink hover:text-white">{tr("View Pricing")}{" "}</Link>
          </Reveal>
        </div>
      </section>

      {/* INSIGHTS TEASER */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
          <SectionHead kicker={tr("ESAIA Insights")} title={tr("Evidence-based perspectives on AI assurance.")} />
          <Reveal delay={0.15}>
            <Link to={lp(lang, "/insights")} data-testid="insights-view-all" className="group inline-flex items-center gap-2 text-sm font-semibold text-esaia-blue">{tr("View all insights")}{" "}<ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <Stagger className="mt-14 border-t border-esaia-line">
          {articles.slice(0, 3).map(a => <StaggerItem key={a.slug}>
              <Link to={lp(lang, `/insights/${a.slug}`)} data-testid={`insight-card-${a.slug}`} className="group grid grid-cols-1 gap-3 border-b border-esaia-line py-9 transition-colors hover:bg-white sm:grid-cols-12 sm:items-baseline sm:gap-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-esaia-subtle sm:col-span-3">
                  <span className="text-esaia-blue">{tr(a.category)}</span> · {tr(a.readTime)}
                </div>
                <h3 className="text-lg font-semibold leading-snug text-esaia-ink transition-colors group-hover:text-esaia-blue sm:col-span-7">
                  {tr(a.title)}
                </h3>
                <span className="inline-flex items-center gap-2 text-xs font-semibold text-esaia-blue sm:col-span-2 sm:justify-end">{tr("Read article")}{" "}<ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </StaggerItem>)}
        </Stagger>
      </section>

      <CTABand title={tr("Ready to understand your AI environment?")} copy={tr("Start with the free Ethical Readiness Index, or request a quotation for ESAIA certification tailored to your organization.")} primary={{
      to: "/eri",
      label: tr("Take the Free ERI"),
      testid: "cta-band-eri"
    }} secondary={{
      to: "/contact",
      label: tr("Request a Quote"),
      testid: "cta-band-quote"
    }} />
    </div>;
}
